import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Flex,
  Typography,
  Input,
  Button,
  Table,
  Tag,
  Row,
  Col,
  Collapse,
  Card,
  Segmented,
  Spin,
} from 'antd';
import {
  SearchOutlined,
  ClearOutlined,
  FilterOutlined,
  DownOutlined,
  UpOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
} from '@ant-design/icons';
import DashboardService from '../../service';
import { Link, useParams } from 'react-router-dom';
import LotCard from '../../components/LotCard';
import VinCopy from '../../components/VinCopy';
import LotCopy from '../../components/LotCopy';
import { useIsMobile } from '../../hooks/useResponsive';

const LotEventsPage = () => {
  const isMobile = useIsMobile();
  const [lotEvents, setLotEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchFilters, setSearchFilters] = useState({});
  const [isSearching, setIsSearching] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grouped');
  const [isGrouped, setIsGrouped] = useState(false);
  const { lang } = useParams();

  useEffect(() => {
    if (isMobile) {
      setViewMode('grouped');
    }
  }, [isMobile]);

  const [searchForm, setSearchForm] = useState({
    quickSearch: '',
    vin: '',
    lotNumber: '',
    buyerNumber: '',
    displayUserName: '',
    appUserName: '',
    appUserID: '',
    event: '',
    platform: '',
    applicationType: '',
    userCity: '',
    userCountry: '',
    userIP: '',
    bid_min: '',
    bid_max: ''
  });

  const observerRef = useRef();
  const loadMoreRef = useRef();
  const debounceRef = useRef();

  const loadEvents = useCallback(async (page = 1, isNewSearch = false, forceSearchFilters = null) => {
    if (loading) return;

    setLoading(true);
    setError('');

    try {
      let data;
      const currentSearchFilters = forceSearchFilters !== null ? forceSearchFilters : searchFilters;
      const shouldSearch = forceSearchFilters !== null
        ? Object.keys(forceSearchFilters).length > 0
        : (isSearching && Object.keys(searchFilters).length > 0);
      const groupByLot = viewMode === 'grouped';

      if (shouldSearch) {
        data = await DashboardService.searchLotEvents({
          ...currentSearchFilters,
          page,
          limit: 20,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          groupByLot
        });
      } else {
        data = await DashboardService.loadLotEvents({
          page,
          limit: 20,
          sortBy: 'createdAt',
          sortOrder: 'desc',
          groupByLot
        });
      }

      const events = data.lots || data.bids || data.events || data.lotEvents || data;
      const pagination = data.pagination;
      const grouped = data.grouped || false;

      setIsGrouped(grouped);

      if (page === 1 || isNewSearch) {
        setLotEvents(events);
        setCurrentPage(1);
      } else {
        setLotEvents(prev => [...prev, ...events]);
      }

      if (pagination) {
        setHasMore(pagination.hasNextPage);
        setCurrentPage(pagination.currentPage);
      } else {
        setHasMore(events.length >= 20);
      }
    } catch (err) {
      console.error('Error loading lot events:', err);
      setError(err.message || 'Failed to load lot events. Please try again later.');
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [loading, isSearching, searchFilters, viewMode]);

  useEffect(() => {
    if (!loadMoreRef.current) return;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading && !initialLoading) {
          loadEvents(currentPage + 1);
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current.observe(loadMoreRef.current);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadEvents, hasMore, loading, initialLoading, currentPage]);

  useEffect(() => {
    loadEvents(1, true);
  }, []);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const debouncedQuickSearch = useCallback((searchTerm) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (searchTerm && searchTerm.trim() !== '') {
        const filters = { quickSearch: searchTerm.trim() };
        setSearchFilters(filters);
        setIsSearching(true);
        setCurrentPage(1);
        loadEvents(1, true, filters);
      } else {
        clearSearch();
      }
    }, 300);
  }, [loadEvents]);

  const handleQuickSearch = (value) => {
    setSearchForm(prev => ({ ...prev, quickSearch: value }));
    clearTimeout(debounceRef.current);

    if (value && value.trim() !== '') {
      debouncedQuickSearch(value.trim());
    } else {
      clearSearch();
    }
  };

  const handleAdvancedSearch = () => {
    clearTimeout(debounceRef.current);

    const filters = {};
    Object.entries(searchForm).forEach(([key, value]) => {
      if (key !== 'quickSearch' && value && value.toString().trim() !== '') {
        filters[key] = value.toString().trim();
      }
    });

    if (Object.keys(filters).length > 0) {
      setSearchFilters(filters);
      setIsSearching(true);
      setCurrentPage(1);
      loadEvents(1, true, filters);
    } else {
      clearSearch();
    }
  };

  const clearSearch = () => {
    clearTimeout(debounceRef.current);
    setSearchFilters({});
    setIsSearching(false);
    setCurrentPage(1);
    setSearchForm({
      quickSearch: '',
      vin: '',
      lotNumber: '',
      buyerNumber: '',
      displayUserName: '',
      appUserName: '',
      appUserID: '',
      event: '',
      platform: '',
      applicationType: '',
      userCity: '',
      userCountry: '',
      userIP: '',
      bid_min: '',
      bid_max: ''
    });
    loadEvents(1, true, {});
  };

  const handleFormChange = (field, value) => {
    setSearchForm(prev => ({ ...prev, [field]: value }));
  };

  const getActiveFilterCount = () => {
    return Object.values(searchFilters).filter(value => value && value.trim() !== '').length;
  };

  const handleViewModeChange = (newMode) => {
    if (newMode) {
      setViewMode(newMode);
      setCurrentPage(1);
      loadEvents(1, true, isSearching ? searchFilters : {});
    }
  };

  const flatColumns = [
    { title: 'Event', dataIndex: 'event', key: 'event' },
    {
      title: 'Buyer',
      key: 'buyer',
      render: (_, record) => (
        <Link
          to={`/${lang}/subdealers?search=${record.appUserCompanyId}.${record.appUserID}`}
          style={{ textDecoration: 'none', color: 'inherit' }}
        >
          {record.appUserName || 'N/A'}
        </Link>
      ),
    },
    {
      title: 'Timestamp',
      key: 'timestamp',
      render: (_, record) =>
        record.timeStamp
          ? new Date(parseInt(record.timeStamp)).toLocaleString()
          : 'N/A',
    },
    {
      title: 'VIN',
      key: 'vin',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <Typography.Text style={{ fontSize: 12 }}>{record.vin || 'N/A'}</Typography.Text>
          {record.vin && <VinCopy vin={record.vin} size="small" />}
        </Flex>
      ),
    },
    {
      title: 'Lot Number',
      key: 'lotNumber',
      render: (_, record) => (
        <Flex align="center" gap={4}>
          <Typography.Text style={{ fontSize: 12 }}>{record.lotNumber || 'N/A'}</Typography.Text>
          {record.lotNumber && <LotCopy lotNumber={record.lotNumber} size="small" />}
        </Flex>
      ),
    },
    { title: 'Bid', dataIndex: 'bid', key: 'bid', render: (v) => v || 'N/A' },
    { title: 'Application Type', dataIndex: 'applicationType', key: 'applicationType', render: (v) => v || 'N/A' },
    {
      title: 'Geolocation',
      key: 'geolocation',
      render: (_, record) =>
        record.userCity
          ? `${record.userCity}, ${record.userCountry} (${record.latitude}, ${record.longitude})`
          : 'N/A',
    },
    { title: 'IP Address', dataIndex: 'userIP', key: 'userIP', render: (v) => v || 'N/A' },
    {
      title: 'User Agent',
      key: 'userAgent',
      render: (_, record) =>
        record.userAgent && record.userAgent.length > 35
          ? `${record.userAgent.substring(0, 35)}...`
          : record.userAgent || 'N/A',
    },
  ];

  const flatDataSource = lotEvents.map((event, index) => ({
    ...event,
    key: event._id || `${event._id}-${index}`,
  }));

  return (
    <div style={{ padding: 20 }}>
      <Flex
        justify="space-between"
        align={isMobile ? 'flex-start' : 'center'}
        style={{ marginBottom: 16 }}
        vertical={isMobile}
        gap={isMobile ? 16 : 0}
      >
        <Flex align="center" gap={8} wrap="wrap" style={{ width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-start' }}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Lot Events
          </Typography.Title>
          {isSearching && (
            <Tag color="blue" closable onClose={clearSearch}>
              {getActiveFilterCount()} filter(s) active
            </Tag>
          )}
        </Flex>

        <Segmented
          value={viewMode}
          onChange={handleViewModeChange}
          size="small"
          options={[
            { value: 'grouped', label: 'Grouped', icon: <AppstoreOutlined /> },
            ...(!isMobile ? [{ value: 'flat', label: 'Flat List', icon: <UnorderedListOutlined /> }] : []),
          ]}
          style={{ width: isMobile ? '100%' : 'auto' }}
        />
      </Flex>

      <Input
        placeholder="Search by VIN, Lot Number, Buyer Number, User Name, City, Country, or IP..."
        prefix={<SearchOutlined />}
        value={searchForm.quickSearch}
        onChange={(e) => handleQuickSearch(e.target.value)}
        suffix={
          searchForm.quickSearch ? (
            <Button type="text" size="small" icon={<ClearOutlined />} onClick={() => handleQuickSearch('')} />
          ) : null
        }
        style={{ marginBottom: 16 }}
      />

      <div style={{ marginBottom: 16 }}>
        <Button
          icon={<FilterOutlined />}
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
        >
          Advanced Filters {showAdvancedFilters ? <UpOutlined style={{ marginLeft: 4 }} /> : <DownOutlined style={{ marginLeft: 4 }} />}
        </Button>
      </div>

      <Collapse
        activeKey={showAdvancedFilters ? ['1'] : []}
        onChange={(keys) => setShowAdvancedFilters(keys.length > 0)}
        style={{ marginBottom: 16 }}
      >
        <Collapse.Panel header="Advanced Filters" key="1">
          <Card>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="VIN - Vehicle Identification Number"
                  value={searchForm.vin}
                  onChange={(e) => handleFormChange('vin', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="Lot Number"
                  value={searchForm.lotNumber}
                  onChange={(e) => handleFormChange('lotNumber', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="Buyer Number"
                  value={searchForm.buyerNumber}
                  onChange={(e) => handleFormChange('buyerNumber', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="Display User Name"
                  value={searchForm.displayUserName}
                  onChange={(e) => handleFormChange('displayUserName', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="App User Name"
                  value={searchForm.appUserName}
                  onChange={(e) => handleFormChange('appUserName', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="App User ID"
                  value={searchForm.appUserID}
                  onChange={(e) => handleFormChange('appUserID', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="Event Type (e.g., bid, watch)"
                  value={searchForm.event}
                  onChange={(e) => handleFormChange('event', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="Platform (web, mobile, app)"
                  value={searchForm.platform}
                  onChange={(e) => handleFormChange('platform', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="Application Type"
                  value={searchForm.applicationType}
                  onChange={(e) => handleFormChange('applicationType', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="City"
                  value={searchForm.userCity}
                  onChange={(e) => handleFormChange('userCity', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="Country"
                  value={searchForm.userCountry}
                  onChange={(e) => handleFormChange('userCountry', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="IP Address"
                  value={searchForm.userIP}
                  onChange={(e) => handleFormChange('userIP', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input
                  type="number"
                  placeholder="Minimum Bid Amount"
                  value={searchForm.bid_min}
                  onChange={(e) => handleFormChange('bid_min', e.target.value)}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Input
                  type="number"
                  placeholder="Maximum Bid Amount"
                  value={searchForm.bid_max}
                  onChange={(e) => handleFormChange('bid_max', e.target.value)}
                />
              </Col>
            </Row>
            <Flex gap={8} style={{ marginTop: 16 }}>
              <Button
                type="primary"
                icon={loading ? <Spin size="small" /> : null}
                onClick={handleAdvancedSearch}
                disabled={loading}
              >
                {loading ? 'Searching...' : 'Apply Filters'}
              </Button>
              <Button onClick={clearSearch} disabled={loading}>
                Clear All
              </Button>
            </Flex>
          </Card>
        </Collapse.Panel>
      </Collapse>

      {error && (
        <Typography.Text type="danger" style={{ display: 'block', textAlign: 'center', margin: '20px 0' }}>
          {error}
        </Typography.Text>
      )}

      {isGrouped && viewMode === 'grouped' ? (
        <div style={{ marginTop: 24 }}>
          {lotEvents.length > 0 ? (
            lotEvents.map((lot, index) => (
              <LotCard key={lot.lotNumber || index} lot={lot} />
            ))
          ) : (
            !initialLoading &&
            !loading && (
              <Card>
                <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', padding: 32 }}>
                  {isSearching ? 'No lots found matching your search criteria.' : 'No lots found.'}
                </Typography.Text>
              </Card>
            )
          )}
        </div>
      ) : (
        <Card style={{ marginTop: 20 }} styles={{ body: { padding: 0 } }}>
          <Table
            columns={flatColumns}
            dataSource={flatDataSource}
            pagination={false}
            scroll={{ x: 1200 }}
            sticky
            locale={{
              emptyText: !initialLoading && !loading
                ? (isSearching ? 'No events found matching your search criteria.' : 'No lot events found.')
                : null,
            }}
          />
        </Card>
      )}

      {initialLoading && (
        <Flex justify="center" align="center" style={{ margin: '40px 0' }}>
          <Spin size="large" />
          <Typography.Text style={{ marginLeft: 16 }}>Loading events...</Typography.Text>
        </Flex>
      )}

      {loading && !initialLoading && (
        <Flex justify="center" align="center" style={{ margin: '20px 0' }}>
          <Spin size="small" />
          <Typography.Text style={{ marginLeft: 16 }}>Loading more events...</Typography.Text>
        </Flex>
      )}

      <div ref={loadMoreRef} style={{ height: 20, margin: '20px 0' }}>
        {!hasMore && lotEvents.length > 0 && (
          <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center' }}>
            No more events to load
          </Typography.Text>
        )}
      </div>
    </div>
  );
};

export default LotEventsPage;
